import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor';
import {
  useEnderecoPorCoordenadaMutation,
  useGetBairrosLojaQuery,
  useGetLojaAtualQuery,
  useGetMunicipiosLojaQuery,
  type TipoEndereco,
} from '../api';
import { CabecalhoModal } from './cabecalho-modal';
import { MapaPino } from './mapa-pino';
import { formatarCep } from '../lib/formato';
import { apenasDigitosCep, buscarCep, cepCompleto } from '../lib/viacep';

/** Tipos de endereço com rótulo e ícone para o seletor e a listagem. */
export const TIPOS_ENDERECO = [
  { valor: 'CASA', rotulo: 'Casa', Icone: HomeOutlinedIcon },
  { valor: 'TRABALHO', rotulo: 'Trabalho', Icone: WorkOutlineIcon },
  { valor: 'OUTROS', rotulo: 'Outros', Icone: PlaceOutlinedIcon },
] as const;

/** Metadados do tipo (com fallback para "Outros" em registros antigos sem tipo). */
export function metaTipoEndereco(tipo: string | null | undefined): (typeof TIPOS_ENDERECO)[number] {
  return TIPOS_ENDERECO.find((t) => t.valor === tipo) ?? TIPOS_ENDERECO[2];
}

/** Endereço pronto para gravar (conta) ou usar direto no pedido (sem login). */
export interface EnderecoPreenchido {
  tipo: TipoEndereco;
  logradouro: string;
  numero: string;
  cep?: string;
  bairro: string;
  complemento?: string;
  cidade: string;
  uf: string;
  latitude?: number;
  longitude?: number;
}

/** Formulário enxuto de endereço — a cidade/UF vêm do combo de municípios atendidos. */
interface EstadoForm {
  idMunicipio: number | '';
  tipo: TipoEndereco;
  logradouro: string;
  numero: string;
  cep: string;
  bairro: string;
  complemento: string;
  /** Coordenada do GPS, quando o cliente usa "minha localização". Salva junto
   * do endereço: a taxa por distância passa a sair da posição real dele. */
  latitude?: number;
  longitude?: number;
}

function estadoInicial(inicial?: Partial<EnderecoPreenchido>): EstadoForm {
  return {
    idMunicipio: '',
    tipo: inicial?.tipo ?? 'CASA',
    logradouro: inicial?.logradouro ?? '',
    numero: inicial?.numero ?? '',
    cep: inicial?.cep ?? '',
    bairro: inicial?.bairro ?? '',
    complemento: inicial?.complemento ?? '',
    latitude: inicial?.latitude,
    longitude: inicial?.longitude,
  };
}

interface Props {
  /** Endereço em edição; o município é casado por cidade/UF quando a lista chega. */
  inicial?: Partial<EnderecoPreenchido>;
  titulo: string;
  /** Dentro de um cartão o título acompanha o texto à esquerda. */
  alinharTitulo?: 'center' | 'left';
  textoSalvar: string;
  salvando?: boolean;
  /** Sem o seletor Casa/Trabalho — pedido sem login não guarda endereço. */
  semTipo?: boolean;
  onSalvar: (endereco: EnderecoPreenchido) => void;
  onCancelar?: () => void;
}

/**
 * Formulário de endereço de entrega: CEP (ViaCEP), GPS e ajuste no mapa.
 * Rola até si e foca o CEP ao montar — ele costuma abrir fora da vista.
 */
export function FormEndereco({
  inicial,
  titulo,
  alinharTitulo = 'center',
  textoSalvar,
  salvando = false,
  semTipo = false,
  onSalvar,
  onCancelar,
}: Props): JSX.Element {
  const { data: municipios } = useGetMunicipiosLojaQuery();
  const { data: bairros } = useGetBairrosLojaQuery();
  const { data: loja } = useGetLojaAtualQuery();
  /** Muda o que o endereço precisa ter: com KM, o CEP vale mais que o bairro. */
  const cobraPorDistancia = loja?.configuracao.estrategiaTaxa === 'KM';

  const [form, setForm] = useState<EstadoForm>(() => estadoInicial(inicial));
  const [enderecoPorCoordenada] = useEnderecoPorCoordenadaMutation();
  const [buscandoLocalizacao, setBuscandoLocalizacao] = useState(false);
  const [erroLocalizacao, setErroLocalizacao] = useState<string | null>(null);
  const [mapaAberto, setMapaAberto] = useState(false);
  /** Coordenada em edição no mapa — só vira a do endereço ao confirmar. */
  const [pino, setPino] = useState<{ latitude: number; longitude: number } | null>(null);
  const [confirmandoPino, setConfirmandoPino] = useState(false);
  // Busca do CEP (ViaCEP): preenche rua, bairro e município e joga o cliente
  // direto no número, que é o único campo que o CEP não sabe.
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [avisoCep, setAvisoCep] = useState<string | null>(null);
  const refNumero = useRef<HTMLInputElement>(null);
  const refForm = useRef<HTMLDivElement>(null);
  const refCep = useRef<HTMLInputElement>(null);
  // Último CEP (só dígitos) mandado ao ViaCEP: resposta de outro CEP é descartada.
  const refCepPedido = useRef('');
  // Espelho do CEP digitado, lido quando a resposta chega (o `form` da closure é velho).
  const refCepDigitado = useRef(form.cep);
  // Raízes dos obrigatórios, para rolar e focar o primeiro que falta ao salvar.
  const refCampoLogradouro = useRef<HTMLDivElement>(null);
  const refCampoNumero = useRef<HTMLDivElement>(null);
  const refCampoMunicipio = useRef<HTMLDivElement>(null);
  const refCampoBairro = useRef<HTMLDivElement>(null);
  // Só depois da primeira tentativa de salvar os campos vazios aparecem em erro.
  const [tentouSalvar, setTentouSalvar] = useState(false);

  useEffect(() => {
    refCepDigitado.current = form.cep;
  }, [form.cep]);

  // O formulário pode nascer fora da vista (abaixo da lista de endereços, no
  // modal do desktop e no celular): rola até ele e põe o cursor no CEP.
  useEffect(() => {
    const t = setTimeout(() => {
      refForm.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      refCep.current?.focus({ preventScroll: true });
    }, 80);
    return () => clearTimeout(t);
  }, []);

  // O município do form é id; o endereço guarda cidade/UF — casa os dois quando a
  // lista chega. Loja com um único município atendido: já vem selecionado.
  useEffect(() => {
    if (form.idMunicipio !== '' || !municipios) return;
    const casado = inicial?.cidade
      ? municipios.find((m) => m.nome === inicial.cidade && m.uf === inicial.uf)
      : undefined;
    const escolhido = casado ?? (municipios.length === 1 ? municipios[0] : undefined);
    if (escolhido) setForm((f) => ({ ...f, idMunicipio: escolhido.id }));
  }, [municipios, form.idMunicipio, inicial?.cidade, inicial?.uf]);

  function campo(nome: keyof EstadoForm, valor: string | number): void {
    setForm((f) => ({ ...f, [nome]: valor }));
  }

  function selecionarMunicipio(idMunicipio: number): void {
    // Bairros variam por município: troca o município e limpa o bairro escolhido.
    setForm((f) => ({ ...f, idMunicipio, bairro: '' }));
  }

  /**
   * Preenche o endereço a partir do CEP.
   *
   * O município NÃO é texto livre: é o combo dos municípios atendidos pela loja.
   * Então o que vem do ViaCEP é casado com essa lista; sem correspondência, o
   * cliente é avisado de que a loja não entrega ali em vez de ficar com um
   * formulário meio preenchido que só falha ao salvar.
   *
   * O bairro por sua vez só é escrito direto quando é campo livre (cobrança por
   * distância). Na cobrança por bairro ele é um select da área de entrega — e um
   * bairro de fora da lista não teria como ser selecionado.
   */
  async function preencherPeloCep(cepInformado: string): Promise<void> {
    if (!cepCompleto(cepInformado)) return;
    const cep = apenasDigitosCep(cepInformado);
    // O mesmo CEP já em busca não vai de novo; um CEP diferente não espera o anterior.
    if (buscandoCep && cep === refCepPedido.current) return;
    // Vale só a resposta do último CEP pedido, e enquanto ele ainda é o do campo.
    const vigente = (): boolean =>
      refCepPedido.current === cep && apenasDigitosCep(refCepDigitado.current) === cep;

    // Busca descartada ou falha libera o CEP: redigitá-lo tem de buscar de novo.
    const liberar = (): void => {
      if (refCepPedido.current === cep) refCepPedido.current = '';
    };
    // O que o cliente tinha no pedido: se ele mexer durante a busca, vale o que digitou.
    const logradouroNoPedido = form.logradouro;
    const bairroNoPedido = form.bairro;
    const intocado = (atual: string, doPedido: string): boolean => atual === doPedido || !atual.trim();

    refCepPedido.current = cep;
    setBuscandoCep(true);
    setAvisoCep(null);
    try {
      const encontrado = await buscarCep(cep);
      if (!vigente()) {
        liberar();
        return;
      }
      if (!encontrado) {
        setAvisoCep('CEP não encontrado. Confira o número ou preencha à mão.');
        return;
      }

      const municipio = municipios?.find(
        (m) => m.nome.trim().toLowerCase() === encontrado.municipio.trim().toLowerCase(),
      );
      // A lista de bairros vem casada por nome de município + UF (não há id).
      const bairroDaLista = bairros?.find(
        (b) =>
          b.municipio.trim().toLowerCase() === encontrado.municipio.trim().toLowerCase() &&
          b.uf.trim().toUpperCase() === encontrado.uf.trim().toUpperCase() &&
          b.nome.trim().toLowerCase() === encontrado.bairro.trim().toLowerCase(),
      );

      setForm((f) => {
        // Por bairro, trocar de município invalida o bairro escolhido (como em selecionarMunicipio).
        const municipioMudou = !!municipio && municipio.id !== f.idMunicipio;
        return {
          ...f,
          // Só sobrescreve o que veio e o que o cliente não mexeu durante a busca.
          logradouro:
            encontrado.logradouro && intocado(f.logradouro, logradouroNoPedido) ? encontrado.logradouro : f.logradouro,
          bairro: cobraPorDistancia
            ? encontrado.bairro && intocado(f.bairro, bairroNoPedido)
              ? encontrado.bairro
              : f.bairro
            : bairroDaLista?.nome ?? (municipioMudou ? '' : f.bairro),
          idMunicipio: municipio?.id ?? f.idMunicipio,
        };
      });

      if (!municipio) {
        setAvisoCep(
          `Este CEP fica em ${encontrado.municipio || 'outra cidade'}, que não está na lista de entrega da loja.`,
        );
        return;
      }
      if (!cobraPorDistancia && encontrado.bairro && !bairroDaLista) {
        setAvisoCep(`O bairro ${encontrado.bairro} não tem entrega configurada — escolha um da lista.`);
      }

      // O CEP resolve tudo menos o número; só leva até lá quem ainda está no CEP.
      if (document.activeElement === refCep.current) refNumero.current?.focus();
    } catch {
      if (vigente()) setAvisoCep('Não foi possível consultar o CEP agora. Preencha o endereço à mão.');
      liberar();
    } finally {
      // Uma busca mais nova ainda em curso mantém o indicador ligado.
      if (refCepPedido.current === cep || refCepPedido.current === '') setBuscandoCep(false);
    }
  }

  /**
   * Escreve no formulário o endereço que o mapa devolveu para uma coordenada.
   *
   * Só sobrescreve campo que veio preenchido: o Google nem sempre sabe o número
   * da casa, e apagar o que o cliente digitou seria pior que manter. Devolve a
   * mensagem de erro quando a coordenada cai fora dos municípios atendidos.
   */
  async function aplicarCoordenada(latitude: number, longitude: number): Promise<string | null> {
    const encontrado = await enderecoPorCoordenada({ latitude, longitude }).unwrap();
    const municipio = municipios?.find(
      (m) => m.nome.toLowerCase() === (encontrado.municipio ?? '').toLowerCase(),
    );
    if (!municipio) {
      return `Este ponto fica em ${encontrado.municipio ?? 'outra cidade'}, onde esta loja não entrega.`;
    }

    setForm((f) => ({
      ...f,
      idMunicipio: municipio.id,
      logradouro: encontrado.logradouro ?? f.logradouro,
      numero: encontrado.numero ?? f.numero,
      bairro: encontrado.bairro ?? f.bairro,
      cep: encontrado.cep ? formatarCep(encontrado.cep) : f.cep,
      latitude: encontrado.latitude,
      longitude: encontrado.longitude,
    }));
    return null;
  }

  /**
   * Fecha o mapa aplicando o pino. Mover o pino REESCREVE rua, número, bairro e
   * CEP: o endereço é o que vai impresso para o entregador, e texto de uma rua
   * com coordenada de outra manda alguém no lugar errado.
   */
  async function confirmarPino(): Promise<void> {
    if (!pino) return setMapaAberto(false);
    setConfirmandoPino(true);
    try {
      const recusa = await aplicarCoordenada(pino.latitude, pino.longitude);
      if (recusa) {
        setErroLocalizacao(recusa);
        return;
      }
      setErroLocalizacao(null);
      setMapaAberto(false);
    } catch {
      // Sem resposta do mapa, ao menos a coordenada escolhida é guardada — ela
      // é o que o cálculo da taxa usa.
      setForm((f) => ({ ...f, latitude: pino.latitude, longitude: pino.longitude }));
      setMapaAberto(false);
    } finally {
      setConfirmandoPino(false);
    }
  }

  /**
   * Preenche o endereço a partir do GPS. O que realmente importa não é poupar
   * digitação: é a COORDENADA ficar salva junto do endereço — aí a taxa por
   * distância sai da posição real do cliente, sem depender de o mapa acertar o
   * texto digitado. O cliente ainda confere e corrige o número.
   */
  async function usarMinhaLocalizacao(): Promise<void> {
    setErroLocalizacao(null);
    if (!navigator.geolocation) {
      setErroLocalizacao('Seu navegador não permite compartilhar a localização.');
      return;
    }
    setBuscandoLocalizacao(true);
    try {
      const posicao = await new Promise<GeolocationPosition>((ok, falhou) =>
        navigator.geolocation.getCurrentPosition(ok, falhou, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }),
      );
      // Fora dos municípios atendidos a loja não entrega, e cidade/UF do
      // formulário não teriam de onde sair.
      const recusa = await aplicarCoordenada(posicao.coords.latitude, posicao.coords.longitude);
      if (recusa) setErroLocalizacao(recusa);
    } catch (err) {
      const negado = typeof err === 'object' && err && 'code' in err && (err as GeolocationPositionError).code === 1;
      setErroLocalizacao(
        negado
          ? 'Permissão de localização negada. Você pode digitar o endereço normalmente.'
          : 'Não foi possível obter sua localização. Digite o endereço.',
      );
    } finally {
      setBuscandoLocalizacao(false);
    }
  }

  const municipioSelecionado = municipios?.find((m) => m.id === form.idMunicipio);

  // Bairros cadastrados (com taxa) do município selecionado.
  const bairrosDoMunicipio = (bairros ?? []).filter(
    (b) =>
      municipioSelecionado &&
      b.municipio.toLowerCase() === municipioSelecionado.nome.toLowerCase() &&
      b.uf === municipioSelecionado.uf,
  );

  // O que falta, na ordem visual do formulário (é a ordem do foco ao salvar).
  const faltando = {
    logradouro: !form.logradouro.trim(),
    numero: !form.numero.trim(),
    municipio: !municipioSelecionado,
    bairro: !form.bairro.trim(),
  };
  const erro = {
    logradouro: tentouSalvar && faltando.logradouro,
    numero: tentouSalvar && faltando.numero,
    municipio: tentouSalvar && faltando.municipio,
    bairro: tentouSalvar && faltando.bairro,
  };

  /** Leva o cliente até o campo: rola até ele e põe o foco no que dá para editar. */
  function irPara(raiz: HTMLDivElement | null): void {
    if (!raiz) return;
    raiz.scrollIntoView({ behavior: 'smooth', block: 'center' });
    raiz
      .querySelector<HTMLElement>('[role="combobox"], input:not([aria-hidden="true"])')
      ?.focus({ preventScroll: true });
  }

  function salvar(): void {
    if (faltando.logradouro || faltando.numero || faltando.municipio || faltando.bairro || !municipioSelecionado) {
      // Botão sempre ativo: o clique explica o que falta em vez de não fazer nada.
      setTentouSalvar(true);
      const primeiro = faltando.logradouro
        ? refCampoLogradouro
        : faltando.numero
          ? refCampoNumero
          : faltando.municipio
            ? refCampoMunicipio
            : refCampoBairro;
      irPara(primeiro.current);
      return;
    }
    onSalvar({
      tipo: form.tipo,
      logradouro: form.logradouro.trim(),
      numero: form.numero.trim(),
      cep: form.cep.trim() || undefined,
      bairro: form.bairro.trim(),
      complemento: form.complemento.trim() || undefined,
      // Cidade e UF vêm do município atendido selecionado.
      cidade: municipioSelecionado.nome,
      uf: municipioSelecionado.uf,
      latitude: form.latitude,
      longitude: form.longitude,
    });
  }

  return (
    <Box ref={refForm} sx={{ mt: 1, scrollMarginTop: 16 }}>
      <Typography variant="subtitle1" align={alinharTitulo} sx={{ fontWeight: 700 }} gutterBottom>
        {titulo}
      </Typography>
      <Stack spacing={2}>
        {!semTipo && (
          <ToggleButtonGroup
            value={form.tipo}
            exclusive
            onChange={(_, valor) => valor && campo('tipo', valor)}
            fullWidth
            size="small"
            aria-label="Tipo do endereço"
          >
            {TIPOS_ENDERECO.map(({ valor, rotulo, Icone }) => (
              <ToggleButton key={valor} value={valor} sx={{ gap: 0.75, textTransform: 'none' }}>
                <Icone fontSize="small" />
                {rotulo}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
        {/* Só faz sentido com cobrança por distância: é lá que a
            coordenada vira dinheiro (e é a chave da loja que paga a
            consulta). */}
        {cobraPorDistancia && (
          <Box>
            <Button
              variant="outlined"
              fullWidth
              startIcon={buscandoLocalizacao ? <CircularProgress size={16} /> : <PlaceOutlinedIcon />}
              onClick={() => void usarMinhaLocalizacao()}
              disabled={buscandoLocalizacao}
            >
              {buscandoLocalizacao ? 'Localizando…' : 'Usar minha localização'}
            </Button>
            {erroLocalizacao ? (
              <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setErroLocalizacao(null)}>
                {erroLocalizacao}
              </Alert>
            ) : (
              form.latitude != null && (
                <Alert
                  severity="success"
                  sx={{ mt: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setPino({ latitude: form.latitude!, longitude: form.longitude! });
                        setMapaAberto(true);
                      }}
                    >
                      Ajustar no mapa
                    </Button>
                  }
                >
                  Localização capturada — confira o número e o complemento.
                </Alert>
              )
            )}
          </Box>
        )}

        {/* CEP na frente: com a busca, ele preenche rua, bairro e município
            e manda o cliente direto para o número. */}
        <TextField
          label={cobraPorDistancia ? 'CEP' : 'CEP (opcional)'}
          value={form.cep}
          onChange={(e) => {
            const cep = formatarCep(e.target.value);
            campo('cep', cep);
            setAvisoCep(null);
            // CEP completo busca sozinho; o mesmo CEP recém-buscado só vai de novo pela lupa/Enter.
            if (cepCompleto(cep) && apenasDigitosCep(cep) !== refCepPedido.current) {
              refCepDigitado.current = cep;
              void preencherPeloCep(cep);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void preencherPeloCep(form.cep);
            }
          }}
          fullWidth
          autoComplete="postal-code"
          inputRef={refCep}
          inputProps={{ inputMode: 'numeric', maxLength: 9 }}
          placeholder="00000-000"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Buscar endereço pelo CEP">
                  {/* <span> porque Tooltip não anexa em botão desabilitado. */}
                  <span>
                    <IconButton
                      edge="end"
                      onClick={() => void preencherPeloCep(form.cep)}
                      disabled={!cepCompleto(form.cep) || buscandoCep}
                      aria-label="Buscar endereço pelo CEP"
                    >
                      {buscandoCep ? <CircularProgress size={20} /> : <YoutubeSearchedForIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          // Com cobrança por distância o CEP deixa de ser enfeite: é o que
          // faz o mapa achar a casa certa, e endereço mal localizado não
          // calcula taxa nenhuma.
          helperText={
            buscandoCep
              ? 'Buscando endereço…'
              : cobraPorDistancia
                ? 'Informe para a entrega ser calculada corretamente.'
                : undefined
          }
        />
        {avisoCep && (
          <Alert severity="warning" onClose={() => setAvisoCep(null)}>
            {avisoCep}
          </Alert>
        )}
        <Stack direction="row" spacing={2}>
          <TextField
            label="Endereço"
            value={form.logradouro}
            onChange={(e) => campo('logradouro', e.target.value)}
            required
            placeholder="Ex: Rua Marli Scotti"
            sx={{ flex: 2 }}
            ref={refCampoLogradouro}
            error={erro.logradouro}
            helperText={erro.logradouro ? 'Informe o endereço.' : undefined}
          />
          <TextField
            label="Núm"
            value={form.numero}
            onChange={(e) => campo('numero', e.target.value)}
            required
            sx={{ flex: 1 }}
            inputRef={refNumero}
            ref={refCampoNumero}
            error={erro.numero}
            helperText={erro.numero ? 'Informe o número.' : undefined}
            // Sem inputMode numérico de propósito: número de casa pode ter
            // letra ("123A") ou ser "S/N" (sem número). Teclado padrão.
          />
        </Stack>
        {/* Município antes do bairro: a lista de bairros depende dele. */}
        <TextField
          select
          label="Município"
          value={form.idMunicipio}
          onChange={(e) => selecionarMunicipio(Number(e.target.value))}
          required
          fullWidth
          ref={refCampoMunicipio}
          error={erro.municipio}
          helperText={
            municipios && municipios.length === 0
              ? 'A loja ainda não configurou os municípios atendidos.'
              : erro.municipio
                ? 'Escolha o município.'
                : undefined
          }
        >
          {(municipios ?? []).map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.nome}
            </MenuItem>
          ))}
        </TextField>
        {/* Por bairro, a lista É a área de entrega: só vale escolher o
            que a loja cadastrou. Por distância, quem decide é o endereço
            no mapa — prender o cliente a uma lista de bairros recusaria
            gente que a loja atende. */}
        {cobraPorDistancia ? (
          <TextField
            label="Bairro"
            value={form.bairro}
            onChange={(e) => campo('bairro', e.target.value)}
            required
            fullWidth
            ref={refCampoBairro}
            error={erro.bairro}
            helperText={erro.bairro ? 'Informe o bairro.' : 'Ajuda a localizar o endereço no mapa.'}
          />
        ) : (
          <TextField
            select
            label="Bairro"
            value={form.bairro}
            onChange={(e) => campo('bairro', e.target.value)}
            required
            fullWidth
            disabled={!municipioSelecionado}
            ref={refCampoBairro}
            error={erro.bairro}
            helperText={
              !municipioSelecionado
                ? 'Escolha o município primeiro.'
                : bairrosDoMunicipio.length === 0
                  ? 'Nenhum bairro com entrega configurada neste município.'
                  : erro.bairro
                    ? 'Escolha o bairro.'
                    : 'Selecione o bairro de entrega.'
            }
          >
            {bairrosDoMunicipio.map((b) => (
              <MenuItem key={b.id} value={b.nome}>
                {b.nome}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          label="Complemento"
          value={form.complemento}
          onChange={(e) => campo('complemento', e.target.value)}
          fullWidth
          placeholder="Ex: Ap 402 ou casa azul"
        />
        <Stack direction="row" spacing={2}>
          <Button variant="contained" onClick={salvar} disabled={salvando}>
            {salvando ? <CircularProgress size={20} /> : textoSalvar}
          </Button>
          {onCancelar && (
            <Button onClick={onCancelar} disabled={salvando}>
              Cancelar
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Ajuste fino do ponto de entrega. O endereço escrito continua o que o
          cliente digitou — o que muda aqui é a coordenada, que é o que o
          cálculo da taxa usa. */}
      <Dialog open={mapaAberto} onClose={() => setMapaAberto(false)} fullWidth maxWidth="sm">
        <CabecalhoModal titulo="Onde entregar" aoFechar={() => setMapaAberto(false)} />
        <DialogContent sx={{ pt: 2 }}>
          <Typography color="text.secondary" variant="body2" sx={{ mb: 1.5 }}>
            Arraste o pino (ou toque no mapa) até a porta de entrada.
          </Typography>
          {pino && (
            <MapaPino
              latitude={pino.latitude}
              longitude={pino.longitude}
              aoMover={(latitude, longitude) => setPino({ latitude, longitude })}
            />
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button color="inherit" onClick={() => setMapaAberto(false)}>
              Cancelar
            </Button>
            <Button variant="contained" disabled={confirmandoPino} onClick={() => void confirmarPino()}>
              {confirmandoPino ? 'Confirmando…' : 'Confirmar localização'}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
